import * as React from 'react';

import { FieldArray, FormikProps } from 'formik';
import Radio from 'antd/es/radio';
import Select from 'antd/es/select';
import Collapse from 'antd/es/collapse';
import * as Yup from 'yup';

import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { PreventDefaultButton } from '@common/react/components/Forms/Button';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { useMobileView } from '@common/react/hooks/useMobileView';
import InputNumber from '@common/react/components/Forms/NumberInput/NumberInput';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';

import { BaseTestSession } from '@commonTuna/react/objects/BaseTestSession';
import { QuestionType } from '@commonTuna/react/objects/BaseDisease';
import { AnswersHistoryButton } from '@commonTuna/react/components/UI/AnswersHistoryButton/AnswersHistoryButton';
import { BasePatient } from '@commonTuna/react/objects/BasePatient';
import CopyForward from '@commonTuna/react/components/UI/QuestionnaireForm/CopyForward';
import PatientSignature from '@commonTuna/react/components/UI/QuestionnaireForm/PatientSignature';
import TextareaWithExpand from '@commonTuna/react/components/UI/TextareaWithExpand/TextareaWithExpand';

import '@commonTuna/react/scss/components/questionnaireForm.scss';

interface QueryParams {
	inviteId: number;
	appointmentId: number | null;
	diseaseId: number;
}

interface ComponentProps {
	session: BaseTestSession | null;
	afterSubmit?: () => void;
	onCancel?: () => void;
	queryParams?: QueryParams;
	enableSave?: boolean;
	hasSign?: boolean;
	showAnswersHistory?: boolean;
	patient?: BasePatient;
	nextBtnOnClick?: () => void;
	saveBtnCaption?: string;
	setSession?: (props: BaseTestSession) => void;
	setHasSign?: (props: boolean) => void;
	copyForwardDisabled?: boolean;
	signatureSrc?: string | undefined;
	setSignatureSrc?: (src: string | undefined) => void;
	testSessions?: Array<BaseTestSession>;
	updateWizardData?: (data) => void;
	fromPortal?: boolean;
	setForm?: (form: FormikProps<any> | null) => void;
	showSave?: boolean;
	beforeForm?: (formikBug: FormikProps<any>) => React.ReactNode;
	afterCopy?: (testSession: BaseTestSession) => void;
	readonly?: boolean;
	onSubmit?: (values: BaseTestSession, actions) => void;
	className?: string;
	formListClassName?: string;
	/**
	 * show all text inside textarea or fixed heigth
	 */
	showAllText?: boolean;
	allowSaveWithoutChanges?: boolean;
}

const validationSchema = Yup.object().shape({
	questions: Yup.array()
		.of(
			Yup.object().shape({
				answerNumber: Yup.mixed().test('is-not-empty', 'Required field!', (value) => value !== null && value !== ''),
			}),
		),
});

type FormValues = BaseTestSession;

const { Option } = Select;

interface QuestionnaireFormHandlerProps {
	session: BaseTestSession | null;
	formikBug: FormikProps<FormValues>;
	setForm?: (form: FormikProps<any> | null) => void;
}

const diagnosisAnswerClassName = 'diagnosis-answer diagnosis-answer_answerBool';

const QuestionnaireFormHandler: React.FC<QuestionnaireFormHandlerProps> = (props) => {
	const { formikBug, session, setForm } = props;
	React.useEffect(() => {
		if (session) {
			formikBug.setValues({ ...session });
		}
	}, [session]);

	React.useEffect(() => {
		if (setForm) {
			setForm(formikBug);
		}
	}, [setForm]);

	return <></>;
};

const QuestionnaireForm: React.FC<ComponentProps> = ({
	session,
	afterSubmit,
	onCancel,
	queryParams = {},
	enableSave = false,
	hasSign,
	showAnswersHistory = false,
	patient,
	nextBtnOnClick,
	saveBtnCaption = 'Save',
	setSession,
	setHasSign,
	copyForwardDisabled,
	signatureSrc,
	setSignatureSrc,
	testSessions,
	updateWizardData,
	fromPortal = true,
	setForm,
	showSave = true,
	beforeForm,
	afterCopy,
	readonly,
	onSubmit,
	className,
	formListClassName,
	showAllText,
	allowSaveWithoutChanges,
}) => {
	const isMobile = useMobileView();
	const { openErrorMessage } = useModal();

	const handleClose = (): void => {
		onCancel && onCancel();
	};

	if (!session) {
		return <Loader />;
	}

	const updateTestSessionsData = (formValues: FormValues, index: number, property: string, value: any) => {
		if (testSessions && updateWizardData) {
			const tempTestSession = formValues;
			const idx = testSessions.findIndex((testSession) => testSession.id === session.id);
			const tempTestSessions = testSessions;

			if (idx >= 0) {
				if (property === 'answerText') {
					tempTestSession.questions[index].answerText = value || null;
				} else if (property === 'answerNumber') {
					tempTestSession.questions[index].answerNumber = value;
				} else if (property === 'answerBool') {
					tempTestSession.questions[index].answerBool = value;
				} else if (property === 'choices') {
					tempTestSession.questions[index].choices = value;
				} else if (property === 'note') {
					tempTestSession.questions[index].note = value || null;
				}
				tempTestSessions[idx] = tempTestSession;
				updateWizardData({
					testSessions: tempTestSessions,
				});
			}
		}
	};

	return <div className={className}>
		<ItemProvider<BaseTestSession>
			id={1}
			item={{
				...session,
				id: 1,
				questions: session.questions,
			}}
			skipInitLoad
			add={{
				...session,
				id: 1,
				questions: session.questions,
			}}
			saveRequest="testSession"
			validationSchema={validationSchema}
			clearForSubmit={({ item }) => {
				const questions = item.questions.filter((question) => {
					const savedDataQuestion = session?.questions.find((q) => q.id === question.id);
					if (savedDataQuestion) {
						return JSON.stringify(question) !== JSON.stringify(savedDataQuestion);
					}
					return false;
				}).map((testAnswer) => {
					const parsedAnswerNumber = parseFloat(testAnswer.answerNumber);
					return {
						...testAnswer,
						answerNumber: parsedAnswerNumber,
						question: null,
						testSession: null,
						choices: testAnswer.choices,
					};
				});
				return {
					questions,
					...queryParams,
					answered: true,
					id: session.id,
				} as any;
			}}
		>
			<ItemEditor
				formikProps={onSubmit ? { onSubmit } : undefined}
				afterSubmit={afterSubmit}
				edit={({ formikBag, state: { loading } }) => {
					const saveBtnDisabled = !allowSaveWithoutChanges
						&& JSON.stringify(formikBag.values.questions) === JSON.stringify(session.questions);

					return <>
						{beforeForm && beforeForm(formikBag)}
						<QuestionnaireFormHandler session={session} formikBug={formikBag} setForm={setForm} />
						<FieldArray
							name="questions"
							render={() => {
								const questionsLength = formikBag.values.questions.length;
								const oddAnswer = questionsLength % 2 === 1;
								const questions = formikBag.values.questions.filter((testAnswer) => !!testAnswer);

								return <div className={formListClassName}>
									{questions.map((testAnswer, index) => {
										const { questionType } = testAnswer;
										return (
											<div
												className={`panel panel-default question-panel ${
													oddAnswer ? 'question-panel_odd' : ''}`}
												key={testAnswer.id}
											>
												<div className="panel-heading">
													{testAnswer.questionText}
													{showAnswersHistory
														&& <div className="pull-right">
															<AnswersHistoryButton
																patient={patient}
																questionId={testAnswer.questionId
																	? testAnswer.questionId : -1}
															/>
														</div>
													}
												</div>
												<div className="panel-body">
													{questionType === QuestionType.Text
														&& <div className="qustionaire-text-area">
															<FormikField
																containerClassName=""
																fieldName={`questions.${index}.answerText`}
																render={({ field, form }) => {
																	return <TextareaWithExpand
																		forceExpand={showAllText}
																		id={field.name}
																		disabled={readonly}
																		className="form-control"
																		{...field}
																		value={field.value === null ? '' : field.value}
																		onChange={(e) => {
																			const value = e.currentTarget.value;
																			form.setFieldValue(
																				field.name,
																				value || null,
																			);
																			updateTestSessionsData(
																				formikBag.values,
																				index,
																				'answerText',
																				value,
																			);
																		}}
																	/>;
																}}
															/>
														</div>
													}
													{questionType === QuestionType.Number
														&& <div className="qustionaire-text-area">
															<FormikField
																containerClassName=""
																fieldName={`questions.${index}.answerNumber`}
																render={({ form, field }) => <InputNumber
																	id={field.name}
																	value={field.value}
																	disabled={readonly}
																	max={9999999}
																	onChange={(value) => {
																		form.setFieldValue(field.name, value);
																		updateTestSessionsData(
																			formikBag.values,
																			index,
																			'answerNumber',
																			value,
																		);
																	}}
																/>}
															/>
														</div>
													}
													{questionType === QuestionType.YesNo
														&& <FormikField
															containerClassName=""
															fieldName={`questions.${index}.answerBool`}
															render={({ field, form }) => (
																<Radio.Group
																	disabled={readonly}
																	style={{ width: '100%' }}
																	onChange={(e) => {
																		form.setFieldValue(field.name, e.target.value);
																		updateTestSessionsData(
																			formikBag.values,
																			index,
																			'answerBool',
																			e.target.value,
																		);
																	}}
																	value={field.value}
																>
																	<Radio
																		value
																		className={diagnosisAnswerClassName}
																	>
																		Yes
																	</Radio>
																	<Radio
																		value={false}
																		className={diagnosisAnswerClassName}
																	>
																		No
																	</Radio>
																</Radio.Group>
															)}
														/>
													}
													{(questionType === QuestionType.Single
															|| questionType === QuestionType.Multiple)
														&& <FormikField
															containerClassName=""
															fieldName={`questions.${index}.choices`}
															render={({ field, form }) => {
																const isMultiple = questionType === QuestionType.Multiple;
																let choiceValue: number | Array<number> | undefined;
																if (field.value) {
																	choiceValue = isMultiple
																		? field.value
																			.filter((ch) => ch.selected)
																			.map((ch) => ch.id)
																		: field.value.filter((ch) => ch.selected)[0]?.id;
																}

																const setValue = (value) => {
																	const resultValue = field.value.map((ch) => ({
																		...ch,
																		selected: isMultiple
																			? value.includes(ch.id) : ch.id === value,
																	}));
																	form.setFieldValue(field.name, resultValue);
																	updateTestSessionsData(
																		formikBag.values,
																		index,
																		'choices',
																		resultValue,
																	);
																};
																return <Select
																	disabled={readonly}
																	mode={isMultiple ? 'multiple' : undefined}
																	style={{ width: '100%' }}
																	onChange={(value) => setValue(value)}
																	value={choiceValue}
																	placeholder="Select the answer"
																>
																	{testAnswer.choices.map((choice) => (
																		<Option key={choice.id} value={choice.id}>
																			<div
																				style={{
																					maxWidth: '100%',
																					overflow: 'hidden',
																					textOverflow: 'ellipsis',
																					whiteSpace: 'nowrap',
																				}}
																				title={choice.answerText}
																			>
																				{choice.answerText}
																			</div>
																		</Option>
																	))}
																</Select>;
															}}
														/>
													}
												</div>
												<div className="question-panel__notes">
													<Collapse
														defaultActiveKey={testAnswer.note ? ['1'] : undefined}
														items={[
															{
																key: '1',
																label: 'Notes',
																children: <>
																	<FormikField
																		containerClassName="qustionaire-text-area"
																		fieldName={`questions.${index}.note`}
																		render={({ field, form }) => {
																			return <TextareaWithExpand
																				disabled={readonly}
																				className="form-control notes"
																				id={field.name}
																				{...field}
																				forceExpand={showAllText}
																				value={field.value === null ? '' : field.value}
																				onChange={(e) => {
																					const value = e.currentTarget.value;
																					form.setFieldValue(
																						field.name,
																						value || null,
																					);
																					updateTestSessionsData(
																						formikBag.values,
																						index,
																						'note',
																						value,
																					);
																				}}
																			/>;
																		}}
																	/>
																</>,
															},
														]}
													/>
												</div>
											</div>
										);
									})}
								</div>;
							}}
						/>
						{enableSave
							? <div className="form-group text-center questionnaire-bottom-buttons-wrapper">
								<span className="flex-wrapper">
									{showSave && <PreventDefaultButton
										title={saveBtnDisabled
											? 'All answers are saved'
											: hasSign ? `${saveBtnCaption}` : 'Patient Sign is required'
										}
										isLoading={loading}
										onClick={() => {
											const questionWithWrongAnswer = formikBag.values.questions
												.find((q) => q.questionType === QuestionType.Number
													&& (q.answerNumber === null || q.answerNumber === ''));
											if (questionWithWrongAnswer) {
												openErrorMessage?.('Answer of number question must be non-empty'
													+ ` (question: "${questionWithWrongAnswer.questionText}")`);
												return;
											}
											formikBag.submitForm();
										}}
										className="btn btn-success mt10 mr10"
										disabled={readonly || saveBtnDisabled || !hasSign}
									>
										{saveBtnCaption}
									</PreventDefaultButton>}
									{nextBtnOnClick
									&& <PreventDefaultButton
										isLoading={loading}
										onClick={nextBtnOnClick}
										className="btn btn-primary mt10"
									>
										Skip
									</PreventDefaultButton>
									}
								</span>
								{session
									&& (
										<span
											className={`questionnaire-bottom-right-buttons flex-wrapper ${
												(showSave || nextBtnOnClick) ? 'mt10' : ''}`}
										>
											{(!fromPortal || !isMobile)
											&& <CopyForward
												session={session}
												afterCopy={(res) => {
													setSession && setSession(res);
													setSignatureSrc && setSignatureSrc(res.files?.list[0]?.file?.src);
													afterCopy && afterCopy(res);
												}}
												caption="Copy Forward"
												disabled={readonly || copyForwardDisabled}
												btnClassName="btn btn-primary mr10"
											/>
											}
											{session.disease?.patientSign
												&& <PatientSignature
													objectId={session.id}
													src={signatureSrc}
													afterSign={(file) => {
														setHasSign && setHasSign(Boolean(file));
														setSignatureSrc && setSignatureSrc(file);
													}}
													caption="Patient Sign"
													fromPortal={fromPortal}
												/>
											}
										</span>
									)
								}
							</div>
							: readonly
								? null
								: (
									<div className="text-center">
										<PreventDefaultButton
											isLoading={loading}
											onClick={handleClose}
											className="btn btn-danger mt10"
										>
											Close
										</PreventDefaultButton>
									</div>
								)
						}
					</>;
				}}
			/>
		</ItemProvider>
	</div>;
};

export default QuestionnaireForm;
